import { BaseRepository } from '@app/src/common/repository/BaseRepository';
import AuthStore from '@app/src/common/store/AuthStore';
import {
  CompanyActionType,
  ISyncUsersJobInfo,
  IUsersSyncSettings
} from '@app/src/components/user-management/model/CompanySettings';

interface ISetting {
  value: string;
}

export interface ISettingItem {
  key: string;
  value: string;
}

export interface IModule {
  id: string;
  key: string;
  name: number;
}

class CompanySettingsRepository extends BaseRepository {
  settingUrl = `${this.userManagementUrl}/companies/`;

  public getCompanySetting(
    module: string,
    key: string,
    companyId: string
  ): Promise<ISetting> {
    const url = `${this.settingUrl}${companyId}/modules/${module}/settings/${key}`;
    return this.callGet<ISetting>(url).then(response => response.data);
  }

  public getCompanySettings(
    module: string,
    keys: string[],
    companyId: string
  ): Promise<ISettingItem[]> {
    const url = `${this.settingUrl}${companyId}/modules/${module}/settings`;
    return this.callGet<ISettingItem[]>(url, { keys: keys.join('|') }).then(
      response => response.data
    );
  }

  public getSetting(module: string, key: string): Promise<ISetting> {
    const url = `${this.settingUrl}${AuthStore.companyId}/modules/${module}/settings/${key}`;
    return this.callGet<ISetting>(url).then(response => response.data);
  }

  public getSettings(module: string, keys: string[]): Promise<ISettingItem[]> {
    const url = `${this.settingUrl}${AuthStore.companyId}/modules/${module}/settings`;
    return this.callGet<ISettingItem[]>(url, { keys: keys.join('|') }).then(
      response => response.data
    );
  }

  public updateCompanySetting(
    module: string,
    key: string,
    companyId: string,
    value: string | null
  ) {
    const url = `${this.settingUrl}${companyId}/modules/${module}/settings/${key}`;
    return this.callPut(url, { value });
  }

  public updateSetting(module: string, key: string, value: string | null) {
    const url = `${this.settingUrl}${AuthStore.companyId}/modules/${module}/settings/${key}`;
    return this.callPut(url, { value });
  }

  public updateBulkSetting(module: string, data: { [key: string]: string }) {
    const url = `${this.settingUrl}${AuthStore.companyId}/modules/${module}/settings`;
    return this.callPut(url, data);
  }

  public getCompanyModules(companyId: string): Promise<IModule[]> {
    return this.callGet<IModule[]>(
      `${this.settingUrl}${companyId}/modules`
    ).then(response => response.data);
  }

  public getCompanyUserSyncSetting(
    companyId: string
  ): Promise<IUsersSyncSettings> {
    return this.callGet<IUsersSyncSettings>(
      `${this.settingUrl}${companyId}/users-sync-settings`
    ).then(response => response.data);
  }

  public updateCompanyUserSyncSetting(
    companyId: string,
    value: IUsersSyncSettings
  ) {
    return this.callPut(
      `${this.settingUrl}${companyId}/users-sync-settings`,
      value
    );
  }

  public syncCompanyUserSyncSetting(companyId: string) {
    return this.callPost(`${this.userManagementUrl}/users/${companyId}/sync`);
  }

  public async getCompanyUserSyncStatus(
    companyId: string,
    actionType: CompanyActionType
  ): Promise<ISyncUsersJobInfo> {
    const response = await this.callGet<ISyncUsersJobInfo>(
      `${this.userManagementUrl}/companies/${companyId}/import-job-info`,
      { actionType }
    );
    return response.data;
  }
}

export default new CompanySettingsRepository();
